import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { cleanText } from '@/utils/sanitize';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Sparkles,
  Loader2,
  FileText,
  Upload,
  X,
  Briefcase,
  Building2,
  Route,
  Lightbulb,
  CheckCircle2,
  Bell,
  Calendar,
  AlertCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import learningPathWorkflowService from '@/services/learning-path-workflow.service';
import { WorkflowCreateRequest } from '@/types/learning-path-workflow.types';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { storageService } from '@/services/storage.service';
import { userService } from '@/services/user.service';
import apiClient from '@/services/api';
import { languageOptions } from '@/constants/languageOptions';

interface WorkflowFormData {
  workflow_name: string;
  workflow_desc?: string;
  group_key: string;
  role_key?: string;
  job_description?: string;
  job_description_file_url?: string;
  job_description_file_type?: 'pdf' | 'docx' | 'txt';
  content_language?: string;
  deadline_days?: number;
  is_mandatory?: boolean;
  notes?: string;
  send_notification?: boolean;
}

// Language preference options with "All Languages" as default
const languagePreferenceOptions = [
  { label: '🌐 All Languages (Recommended)', value: 'all' },
  ...languageOptions,
];

export default function WorkflowCreate() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [inputMethod, setInputMethod] = useState<'text' | 'file'>('text');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string>('');
  const [uploadedFileType, setUploadedFileType] = useState<'pdf' | 'docx' | 'txt' | ''>('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Department and Role state
  const [availableDepartments, setAvailableDepartments] = useState<any[]>([]);
  const [availableRoles, setAvailableRoles] = useState<any[]>([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const pendingRoleKeyRef = useRef<string | null>(null);

  const isEditMode = !!id;

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<WorkflowFormData>({
    mode: 'all',
    defaultValues: {
      workflow_name: '',
      workflow_desc: '',
      group_key: '',
      role_key: '',
      job_description: '',
      content_language: 'all',
      deadline_days: 30,
      is_mandatory: false,
      notes: '',
      send_notification: true,
    },
  });

  // Load existing workflow data when in edit mode
  const { data: existingWorkflow, isLoading: isLoadingWorkflow } = useQuery({
    queryKey: ['workflow', id],
    queryFn: () => learningPathWorkflowService.getWorkflowById(id!),
    enabled: isEditMode,
  });

  const selectedDepartment = watch('group_key');

  // Load departments on mount
  useEffect(() => {
    loadDepartments();
  }, []);

  // Populate form with existing workflow data
  useEffect(() => {
    if (existingWorkflow?.data) {
      const workflow = existingWorkflow.data;

      // Store role_key to restore after roles are loaded (use ref for sync update)
      if (workflow.role_key) {
        pendingRoleKeyRef.current = workflow.role_key;
      }

      reset({
        workflow_name: workflow.workflow_name,
        workflow_desc: workflow.workflow_desc || '',
        group_key: workflow.group_key,
        role_key: workflow.role_key || '',
        job_description: workflow.job_description || '',
        deadline_days: workflow.deadline_days || 30,
        is_mandatory: workflow.is_mandatory || false,
        notes: workflow.notes || '',
        send_notification: workflow.send_notification !== false,
      });

      // Set uploaded file info if exists
      if (workflow.job_description_file_url) {
        setUploadedFileUrl(workflow.job_description_file_url);
        setUploadedFileType(workflow.job_description_file_type || 'pdf');
        setInputMethod('file');
      }
    }
  }, [existingWorkflow, reset]);

  // Load roles when department changes
  useEffect(() => {
    if (selectedDepartment) {
      loadRolesForDepartment(selectedDepartment);
      // Reset role when department changes
      setValue('role_key', '');
    } else {
      setAvailableRoles([]);
    }
  }, [selectedDepartment, setValue]);

  const loadDepartments = async () => {
    try {
      setLoadingDepartments(true);
      const response = await userService.getGroupsDropdown();
      if (response.success && response.data) {
        setAvailableDepartments(response.data || []);
      }
    } catch (error) {
      console.error('Failed to load departments:', error);
      toast.error('Failed to load departments');
    } finally {
      setLoadingDepartments(false);
    }
  };

  const loadRolesForDepartment = async (groupKey: string) => {
    try {
      setLoadingRoles(true);
      const response = await apiClient.get(`/group-role-map/${groupKey}`);
      if (response.data.success && response.data.data) {
        const roles = response.data.data.roles || [];
        setAvailableRoles(roles);

        // Restore pending role_key if it exists in the loaded roles
        if (pendingRoleKeyRef.current && roles.some((r: any) => r.role_key === pendingRoleKeyRef.current)) {
          setValue('role_key', pendingRoleKeyRef.current);
          pendingRoleKeyRef.current = null;
        }
      }
    } catch (error) {
      console.error('Failed to load roles for department:', error);
      setAvailableRoles([]);
    } finally {
      setLoadingRoles(false);
    }
  };

  const createMutation = useMutation({
    mutationFn: (data: WorkflowCreateRequest) => learningPathWorkflowService.createWorkflow(data),
    onSuccess: (response) => {
      toast.success('Workflow created successfully!');
      navigate(`/admin/learning-paths/workflow/${response.data._id}`);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create workflow'
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => learningPathWorkflowService.updateWorkflow(id!, data),
    onSuccess: () => {
      toast.success('Workflow updated successfully!');
      navigate(`/admin/learning-paths/workflow/${id}`);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update workflow'
      );
    },
  });

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a PDF, DOCX, or TXT file');
      return;
    }

    // Validate file size
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      toast.error('File size should be less than 10MB');
      return;
    }

    setUploadedFile(file);
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Upload file to storage
      const response = await storageService.uploadFile(file, {
        onProgress: (progress) => {
          setUploadProgress(progress.percentage);
        },
        metadata: {
          category: 'job-description',
          type: 'workflow',
        },
      });

      // Determine file type from file extension or MIME type
      let fileType: 'pdf' | 'docx' | 'txt' = 'txt';
      if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
        fileType = 'pdf';
      } else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || file.name.endsWith('.docx')) {
        fileType = 'docx';
      }

      // Store the URL and type
      setUploadedFileUrl(response.url);
      setUploadedFileType(fileType);
      setValue('job_description_file_url', response.url);
      setValue('job_description_file_type', fileType);

      toast.success('File uploaded successfully!');
    } catch (error: any) {
      console.error('Upload failed:', error);
      toast.error(error.message || 'Failed to upload file');
      setUploadedFile(null);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
    setUploadedFileUrl('');
    setUploadedFileType('');
    setValue('job_description_file_url', undefined);
    setValue('job_description_file_type', undefined);
  };

  const onSubmit = async (data: WorkflowFormData) => {
    if (isEditMode) {
      // Update mode - only send updatable fields
      const updateData: any = {
        workflow_name: cleanText(data.workflow_name),
        workflow_desc: cleanText(data.workflow_desc),
        group_key: data.group_key,
        role_key: data.role_key,
        deadline_days: data.deadline_days,
        is_mandatory: data.is_mandatory,
        notes: cleanText(data.notes),
        send_notification: data.send_notification,
      };

      updateMutation.mutate(updateData);
    } else {
      // Create mode - validate job description
      if (inputMethod === 'text' && !data.job_description?.trim()) {
        toast.error('Please provide a job description');
        return;
      }
      if (inputMethod === 'file' && !uploadedFileUrl) {
        toast.error('Please upload a job description file');
        return;
      }

      const submitData: any = {
        workflow_name: cleanText(data.workflow_name),
        workflow_desc: cleanText(data.workflow_desc),
        group_key: data.group_key,
        role_key: data.role_key,
        content_language: data.content_language || 'all',
        deadline_days: data.deadline_days,
        is_mandatory: data.is_mandatory,
        notes: cleanText(data.notes),
        send_notification: data.send_notification,
      };

      if (inputMethod === 'text') {
        // Send job description text
        submitData.job_description = cleanText(data.job_description);
      } else if (inputMethod === 'file') {
        // Send uploaded file URL and type
        submitData.job_description_file_url = uploadedFileUrl;
        submitData.job_description_file_type = uploadedFileType;
      }

      createMutation.mutate(submitData);
    }
  };

  const jobDescription = watch('job_description');
  const wordCount = jobDescription?.trim().split(/\s+/).filter(Boolean).length || 0;

  if (isLoadingWorkflow) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl">
            <Route className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isEditMode ? 'Edit' : 'Create'} Corporate Assignment Workflow
            </h1>
            <p className="text-gray-600">
              {isEditMode
                ? 'Update workflow settings and notification preferences'
                : 'Generate learning paths automatically from job descriptions'}
            </p>
          </div>
        </div>
      </div>

      {/* AI Info Alert - Only show in create mode */}
      {!isEditMode && (
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
          <div className="flex gap-3">
            <Sparkles className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-purple-900 mb-1">AI-Powered Workflow Generation</h3>
              <p className="text-sm text-purple-800 mb-2">
                Provide a job description and AI will automatically:
              </p>
              <ul className="text-sm text-purple-800 space-y-1 ml-4">
                <li>• Extract required skills and competencies</li>
                <li>• Create a comprehensive learning path</li>
                <li>• 🌐 Select courses in your preferred language(s)</li>
                <li>• Generate metadata, images, and course selections</li>
                <li>• Sequence courses for optimal learning progression</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Briefcase className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
          </div>

          <div className="space-y-4">
            <Input
              name="workflow_name"
              control={control}
              label="Workflow Name"
              required
              rules={{
                required: 'Workflow name is required',
              }}
              placeholder="e.g., Senior Software Engineer Onboarding"
              error={errors.workflow_name?.message}
            />

            <Textarea
              name="workflow_desc"
              control={control}
              label="Workflow Description"
              rows={3}
              placeholder="Describe the purpose of this workflow..."
              error={errors.workflow_desc?.message}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Department <span className="text-red-500">*</span>
                </label>
                <Select<WorkflowFormData>
                  control={control}
                  name="group_key"
                  rules={{
                    validate: (value) => {
                      if (!value || value === '') return 'Department is required';
                      return true;
                    },
                  }}
                  options={[
                    { value: '', label: loadingDepartments ? 'Loading departments...' : 'Select Department' },
                    ...availableDepartments.map(dept => ({
                      value: dept.group_key,
                      label: dept.group_name
                    }))
                  ]}
                  placeholder="Select Department"
                  isDisabled={loadingDepartments}
                />
                {errors.group_key && (
                  <p className="mt-1 text-sm text-red-600">{errors.group_key.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role
                </label>
                <Select<WorkflowFormData>
                  control={control}
                  name="role_key"
                  options={[
                    { value: '', label: loadingRoles ? 'Loading roles...' : selectedDepartment ? 'Select Role' : 'Select department first' },
                    ...availableRoles.map(role => ({
                      value: role.role_key,
                      label: role.role_name
                    }))
                  ]}
                  placeholder="Select Role"
                  isDisabled={!selectedDepartment || loadingRoles}
                />
                {errors.role_key && (
                  <p className="mt-1 text-sm text-red-600">{errors.role_key.message}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Job Description - Only show in create mode */}
        {!isEditMode && (
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-purple-600" />
              <h2 className="text-lg font-semibold text-gray-900">Job Description</h2>
            </div>

          {/* Input Method Toggle */}
          <div className="flex gap-2 mb-4">
            <button
              type="button"
              onClick={() => {
                setInputMethod('text');
              }}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                inputMethod === 'text'
                  ? 'bg-primary-100 text-primary-600'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Paste Text
            </button>
            <button
              type="button"
              onClick={() => {
                setInputMethod('file');
                clearErrors('job_description');
              }}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                inputMethod === 'file'
                  ? 'bg-primary-100 text-primary-600'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Upload className="w-4 h-4 inline mr-2" />
              Upload File
            </button>
          </div>

          {/* Text Input */}
          {inputMethod === 'text' && (
            <div>
              <Textarea
                name="job_description"
                control={control}
                label="Job Description Text"
                required
                rules={{
                  validate: (value) => {
                    if (!value?.trim()) return 'Job description is required';
                    if (value.trim().length < 50) return 'Job description should be at least 50 characters';
                    return true;
                  },
                }}
                rows={12}
                placeholder="Paste the complete job description here. Include responsibilities, requirements, qualifications, and any other relevant details..."
                error={errors.job_description?.message}
                className="font-mono text-sm"
              />
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className={`${wordCount < 50 ? 'text-gray-400' : 'text-gray-600'}`}>
                  {wordCount} words
                </span>
                {wordCount < 50 && (
                  <span className="text-amber-600 flex items-center gap-1">
                    <Lightbulb className="w-3 h-3" />
                    Recommended: At least 50 words for better results
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Language Selector */}
          <div className="mt-4">
            <Select<WorkflowFormData>
              control={control}
              name="content_language"
              label="Course Language"
              options={languagePreferenceOptions}
              isSearchable={true}
            />
            <p className="mt-1.5 text-xs text-gray-500">
              💡 <strong>"All Languages":</strong> AI will select best courses regardless of language (mixed path).
              <br />
              <strong>Specific language:</strong> Learning path will include ONLY courses in that language.
            </p>
          </div>

          {/* File Upload */}
          {inputMethod === 'file' && (
            <div>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors">
                {!uploadedFile ? (
                  <label className="cursor-pointer block">
                    <input
                      type="file"
                      accept=".pdf,.docx,.txt"
                      onChange={handleFileUpload}
                      className="hidden"
                      disabled={isUploading}
                    />
                    <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-700 font-medium mb-1">
                      Click to upload job description
                    </p>
                    <p className="text-sm text-gray-500">
                      PDF, DOCX, or TXT (max 10MB)
                    </p>
                  </label>
                ) : isUploading ? (
                  <div className="space-y-4">
                    <div className="w-12 h-12 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 truncate max-w-xs">{uploadedFile.name}</span>
                        <span className="text-blue-600 font-medium">{uploadProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-center gap-2 text-green-600 mb-2">
                      <CheckCircle2 className="w-8 h-8" />
                      <p className="font-medium">File uploaded successfully!</p>
                    </div>
                    <div className="flex items-center justify-between bg-blue-50 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <FileText className="w-8 h-8 text-blue-600" />
                        <div className="text-left">
                          <p className="font-medium text-gray-900">{uploadedFile.name}</p>
                          <p className="text-sm text-gray-500">
                            {(uploadedFile.size / 1024).toFixed(2)} KB • {uploadedFileType.toUpperCase()}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeFile}
                        className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                      >
                        <X className="w-5 h-5 text-red-600" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          </div>
        )}

        {/* Notification Settings */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Bell className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-900">Assignment Settings</h2>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                name="deadline_days"
                control={control}
                label="Deadline (Days)"
                type="number"
                placeholder="30"
                error={errors.deadline_days?.message}
                helperText="Number of days to complete after assignment"
              />

              <div className="flex items-center space-x-3 pt-8">
                <input
                  type="checkbox"
                  id="is_mandatory"
                  {...control.register('is_mandatory')}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <label htmlFor="is_mandatory" className="text-sm font-medium text-gray-700">
                  Mark as Mandatory
                </label>
              </div>
            </div>

            <Textarea
              name="notes"
              control={control}
              label="Assignment Notes"
              rows={3}
              placeholder="Add any additional notes or instructions for learners..."
              error={errors.notes?.message}
            />

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="send_notification"
                {...control.register('send_notification')}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <label htmlFor="send_notification" className="text-sm font-medium text-gray-700">
                Send notification when workflow is activated
              </label>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/learning-paths/workflow')}
            className="px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || createMutation.isPending || updateMutation.isPending}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium inline-flex items-center gap-2"
          >
            {(isSubmitting || createMutation.isPending || updateMutation.isPending) ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {isEditMode ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                {isEditMode ? 'Update Workflow' : 'Create Workflow'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
